import { useEffect, useCallback, useContext } from 'react';
import { WalkDataContext } from './hooks/walkDataContext';
import {getStoryMetadataValue} from './utility/MetadataFetcher';

const MediaSession = ({ audioRef }) => {
  const { storyMetas, language, currentTrack, setIsPlaying } = useContext(WalkDataContext);

  const getMetadataValue = useCallback((source, key, defaultValue = '') => {
    return source && source[key] && source[key][language]
      ? source[key][language]
      : defaultValue;
  }, [language]);

  const updateMediaSession = useCallback(() => {
    if (!('mediaSession' in navigator) || !currentTrack) return;

    const getThumbnail = () => getMetadataValue(currentTrack.metadata, 'trackMeta_cover') ||
                               getMetadataValue(storyMetas, 'storyMeta_cover', '/assets/illustrations/placeholder-cover.jpg');

    const getArtist = () => getMetadataValue(currentTrack.metadata, 'trackMeta_author') ||
                            getMetadataValue(storyMetas, 'storyMeta_author');

    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: getMetadataValue(currentTrack.metadata, 'trackMeta_name'),
      artist: getArtist(),
      album: getMetadataValue(storyMetas, 'storyMeta_title'),
      artwork: [{ src: getThumbnail(), sizes: '512x512', type: 'image/png' }]
    });

    navigator.mediaSession.setActionHandler('play', () => {
      audioRef.current?.play();
      setIsPlaying(true);
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      audioRef.current?.pause();
      setIsPlaying(false);
    });
    navigator.mediaSession.setActionHandler('nexttrack', () => {
      if (audioRef.current) audioRef.current.currentTime += 15;
    });
    navigator.mediaSession.setActionHandler('previoustrack', () => {
      if (audioRef.current) audioRef.current.currentTime -= 15;
    });
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (audioRef.current && details.seekTime != null) {
        audioRef.current.currentTime = details.seekTime;
      }
    });
  }, [currentTrack, storyMetas, language, getMetadataValue, setIsPlaying, audioRef]);

  // Hält den OS-Sperrbildschirm über Position und Dauer informiert
  useEffect(() => {
    if (!('mediaSession' in navigator) || !audioRef.current) return;
    const audio = audioRef.current;

    const updatePositionState = () => {
      if (audio.duration && isFinite(audio.duration)) {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          playbackRate: audio.playbackRate,
          position: audio.currentTime,
        });
      }
    };

    const keepSessionAlive = () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise) {
        playPromise.then(() => {
          setTimeout(() => audio.pause(), 1);
        }).catch(() => {});
      }
    };

    const handleEnded = () => {
      navigator.mediaSession.playbackState = 'paused';
      keepSessionAlive();
    };

    audio.addEventListener('timeupdate', updatePositionState);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updatePositionState);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioRef, currentTrack]);

  useEffect(() => {
    if (storyMetas === null) return;

    const title = getStoryMetadataValue(storyMetas, language, 'storyMeta_title', 'Ortsieler');

    const customIcon = getStoryMetadataValue(storyMetas, 1, 'storyMeta_customicon');
    const coverImage = getStoryMetadataValue(storyMetas, language, 'storyMeta_cover');
    const favicon = customIcon || coverImage || '/assets/illustrations/placeholder-cover.jpg';

    document.title = title;

    const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = favicon;
    if (!link.parentNode) document.head.appendChild(link);

    updateMediaSession();
  }, [storyMetas, language, currentTrack, updateMediaSession]);

  return null;
};

export default MediaSession;
